<script>
	import { onMount } from 'svelte';
	import { goto, ready, url } from '@roxi/routify';
	import SvelteTable from "svelte-table";
	import { getData, getGETAPILink, postFormData, searchTypeToName } from "../helpers.js";
	import { activeTabs, featuresLoaded, loadFeatures } from "../stores/integrations.js";

	loadFeatures();
	import Navbar from "../components/Navbar.svelte";
	import Footer from "../components/Footer.svelte";
	import UserInfoCard from "../components/UserInfoCard.svelte";
	import DbTank from "../components/DbTank.svelte";

	let historyErrorField = '';

	let recentSearchData = [];

	let historyLoaded = false;
	let recentSearchDataPromise = getData('search/historical', {'page': 0, page_limit: 5})
	recentSearchDataPromise.then(response => response.json())
	.then(ret_json => {
		if (ret_json.status === 'success') {
			recentSearchData = ret_json.data;
			historyLoaded = true;
		} else {
			historyErrorField = 'An Unknown Error Occurred';
		}
	});

	// Database Status tanks: server decides which tanks the user sees
	// (entitlements are checked in the db/metrics endpoint, not here).
	let dbTanks = [];
	let dbTanksLoaded = false;
	getData('db/metrics').then(response => response.json())
	.then(ret_json => {
		if (ret_json.status === 'success') {
			dbTanks = ret_json.tanks;
		}
		dbTanksLoaded = true;
	})
	.catch(() => { dbTanksLoaded = true; });

	Promise.allSettled([recentSearchDataPromise])
	.then(() => {
		// this is not SSR, so it doesn't actually do anything. Go figure. - HC
		$ready();
	});

	// Recent History Code
	let sortBy = "creation_time";
	let sortOrder = 1;
	let iconAsc = "↑";
	let iconDesc = "↓";
	let numRows = 5;
	
	let dateOptions = {}
	const COLUMNS = {
		id: {
			key: "id",
			title: "Search ID",
			value: v => v.id.slice(0,10),
			sortable: false,
			headerClass: "bg-gray-200",
		},
		creationTime: {
			key: "creationTime",
			title: "Search Time",
			value: v => new Date(v.creation_time*1000).toLocaleString('en-US', { 
				month: 'numeric', 
				day: 'numeric', 
				year: 'numeric', 
				hour: 'numeric', 
				minute: '2-digit', 
				hour12: true 
			}),
			sortable: true,
			headerClass: "bg-gray-200",
		},
		searchType: {
			key: "searchType",
			title: "Search Type",
			value: v => searchTypeToName(v.search_type),
			sortable: true,
			headerClass: "bg-gray-200",
		},
		statusName: {
			key: "statusName",
			title: "Status",
			value: v => v.status_name,
			sortable: true,
			headerClass: "bg-gray-200",
		},
		downloadMetadata: {
			key: "downloadMetadata",
			title: "Metadata",
			parseHTML: true,
			value: v => {
				//commented out per Henry's unmerged edits May 22 -- however, some edits are here which idictaes possibly this should be uncommented 8/3/2025
				if (v.status_name !== 'ERROR') {
					return `<a href="${getGETAPILink("search/metadata", {"search_id":v.id })}" download="${v.id + "_metadata.txt"}" target="_self"><button class="w-full border-none">⭳</button></a>`;
				} else {
					return 'X'
				}
			},
			sortable: false,
			headerClass: "bg-gray-200",
		},
		downloadFile: {
			key: "downloadFile",
			title: "File",
			parseHTML: true,
			value: v => {
				// Hide zip for uploads (type 6)
				if (v.search_type == 6) {
					return '-';
				}
				// let type = searchTypeToName(v.search_type);
				if (v.status_name === 'FINISHED' || v.status_name === 'ARCHIVED') {
					const ext = v.search_type == 10 ? '.pdf' : '.zip';
					return `<a href="${getGETAPILink("search/download", {"search_id":v.id })}" download="${v.id + ext}" target="_self"><button class="w-full border-none">⭳</button></a>`;
				} else if (v.status_name === 'ERROR') {
					return 'X';
				} else {
					return '-';
				}
			},
			sortable: false,
			headerClass: "bg-gray-200",
		},
	};
	let selectedDesktopCols = ["id", "searchType", "creationTime", "statusName", "downloadMetadata", "downloadFile"];
	$: desktopCols = selectedDesktopCols.map(key => COLUMNS[key]);

	let selectedMobileCols = ["id", "statusName"];
	$: mobileCols = selectedMobileCols.map(key => COLUMNS[key]);

	let expandedArr = [];
	function handleRowClick(event) {
		// manually toggle expanded items
		const row = event.detail.row;
		if (!row.$expanded) {
			expandedArr = [...expandedArr, row.id];
		} else {
			expandedArr = expandedArr.filter(id => id !== row.id);
		}
	}
</script>

<main class="background">
	<Navbar/>
	<div class="lg:mx-48 md:mx-32 sm:mx-8">
		<div class="flex flex-col space-y-4 mt-10 items-stretch lg:items-stretch lg:flex-row lg:space-y-0 lg:space-x-4">
			<div class="mx-2 lg:w-4/12">
				<UserInfoCard/>
			</div>
			<div class="mx-2 lg:!mb-0 px-8 py-4 lg:w-8/12 lg:px-16 lg:py-16 card">
				<div class="flex flex-wrap items-center justify-between mb-6">
					<p class="text-2xl font-semibold text-blue-900 mt-4 lg:mt-0">Integration library</p>
					<button on:click={() => $goto('/integrations')}
						class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
						Manage Integrations</button>
				</div>
				{#if !$featuresLoaded}
					<!-- Entitlements load async (user/paywall-features); rendering
					     $activeTabs before then flashes freemium-only tiles. -->
					<p class="text-xl text-gray-400">Loading integrations...</p>
				{:else if $activeTabs.length !== 0}
					<div class="flex flex-wrap gap-4">
						{#each $activeTabs as tab}
							<a href={$url(tab.path)} class="flex items-center border rounded-lg px-4 py-3 bg-white shadow-sm hover:bg-gray-50 text-gray-800">
								<svg width="32" height="32" viewBox="0 0 48 48" class="mr-3 shrink-0">
									<rect width="48" height="48" rx="10" fill={tab.color}/>
									<text x="24" y="24" text-anchor="middle" dominant-baseline="central"
										fill="white" font-family="monospace" font-weight="bold"
										font-size={tab.monogram.length > 3 ? 11 : 14}>
										{tab.monogram}
									</text>
								</svg>
								<span class="text-lg font-semibold">{tab.name}</span>
							</a>
						{/each}
					</div>
				{:else}
					<p class="text-xl">No active integrations. Visit the Integrations tab to add data sources.</p>
				{/if}
			</div>
		</div>
		<div class="mx-2 mt-4 px-8 py-6 lg:px-16 lg:py-10 card z-[90]">
			<p class="text-2xl font-semibold text-blue-900 mb-2">Recent Searches</p>
			<p class="text-md mb-8 block lg:hidden">(Click to Expand)</p>
			<p class="text-xl text-red-500">{historyErrorField}</p>
			{#if recentSearchData.length !== 0}
				<!-- Shown on large screen -->
				<div class="hidden lg:block">
					<SvelteTable
						columns={desktopCols}
						rows={recentSearchData}
						classNameRow={'text-lg text-center odd:bg-white even:bg-gray-200'}
						classNameThead={['table-primary sticky top-0 bg-white']}
						classNameTbody={['w-full overflow-scroll']}
						classNameSelect={['rounded-md shadow-sm font-thin py-1 px-2']}
					/>
				</div>
				<!-- Shown on mobile -->
				<div class="block lg:hidden">
					<SvelteTable
						columns={mobileCols}
						rows={recentSearchData}
						classNameRow={'cursor-pointer text-lg text-center'}
						classNameThead={['table-primary sticky top-0 bg-white']}
						classNameTbody={['w-full overflow-scroll']}
						classNameSelect={['rounded-md shadow-sm font-thin py-1 px-2']}
						classNameRowExpanded={'bg-gray-400'}
						classNameExpandedContent={'bg-gray-200'}
						classNameCell={'py-2'}
						expandRowKey="id"
						bind:expanded={expandedArr}
						on:clickRow={handleRowClick}
					>
					<svelte:fragment slot="expanded" let:row>
						<p class="text-center">Search Time: {new Date(row.creation_time*1000).toLocaleString('en-US')}</p>
						{#if row.status_name !== 'ERROR'}
							<a class="text-blue-500 visited:text-blue-500" href="{getGETAPILink("search/metadata", {"search_id":row.id })}" download="{row.id}_metadata.txt" target="_self">
								<button class="w-full border-none">⭳ Download Metadata ⭳</button>
							</a>
							{#if row.status_name === 'FINISHED'}
								<a class="text-blue-500 visited:text-blue-500" href="{getGETAPILink("search/download", {"search_id":row.id })}" download="{row.id}_metadata.txt" target="_self">
									<button class="w-full border-none">⭳ Download File ⭳</button>
								</a>
							{/if}
						{/if}
					</svelte:fragment>
					</SvelteTable>
				</div>
			{:else if historyLoaded}
				<p class="text-xl">No Recent Data Found</p>
			{/if}
		</div>
		<div class="mx-2 mt-4 px-8 py-6 lg:px-4 lg:py-10 card">
			<p class="text-2xl font-semibold text-blue-900 mb-6">Database Status</p>
			{#if dbTanks.length !== 0}
				<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{#each dbTanks as tank (tank.id)}
						<DbTank {tank}/>
					{/each}
				</div>
			{:else if dbTanksLoaded}
				<p class="text-xl">Database metrics unavailable.</p>
			{/if}
		</div>
		<Footer/>
	</div>
</main>

<style>
	main.background { 
		background-color:theme("colors.gray.100");
	}
</style>